// Dana's proactive opening message — shown when the chat panel opens on an empty
// thread, so Dana "speaks first" with a context-aware nudge instead of a blank
// box. Deterministic (no LLM call on open): the line is derived from the current
// page + role, and for admins from real setup progress.
import React from 'react';
import { useSetupProgress } from '@/features/onboarding/setup/useSetupProgress';
import { DanaAvatar } from './branding/DanaAvatar';

const PAGE_GREETING: Record<string, string> = {
  'Time Off': 'Want to request leave or check your balance?',
  'Leave Approvals': "Want help reviewing your team's leave requests?",
  'My Pay': 'Want me to explain your latest payslip?',
  'Employee Directory': 'Looking for someone, or want to see the org chart?',
  'My Team': 'Want a quick snapshot of your team?',
  'Payroll Runs': 'Need a hand with a payroll run?',
  'Loans & Advances': 'Need help with a loan or advance?',
  'Documents': 'Need to find or upload a document?',
  Administration: 'What would you like to manage today?',
  'Set up your company': 'Want help finishing your company setup?',
  'My Profile': 'Want to update your details?',
};
const DEFAULT_GREETING = 'How can I help with your HR today?';

const Bubble: React.FC<{ name?: string; line: string }> = ({ name, line }) => (
  <div className="flex justify-start">
    <div className="max-w-[85%] rounded-2xl px-3 py-2 text-sm bg-card border border-border text-foreground">
      <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
        <DanaAvatar size={18} /> Dana
      </span>
      <p className="mt-1">
        {name ? `Hi ${name}! 👋 ` : 'Hi! 👋 '}
        {line}
      </p>
    </div>
  </div>
);

/** Greeting for non-admins (no data fetch). */
const BasicGreeting: React.FC<{ page: string; name?: string }> = ({ page, name }) => (
  <Bubble name={name} line={PAGE_GREETING[page] || DEFAULT_GREETING} />
);

/** Greeting for admins — folds in real setup progress when it isn't complete. */
const AdminGreeting: React.FC<{ page: string; name?: string }> = ({ page, name }) => {
  const { loading, allRequiredDone, doneCount, total } = useSetupProgress();
  let line = PAGE_GREETING[page] || DEFAULT_GREETING;
  if (!loading && !allRequiredDone) {
    line = `you've completed ${doneCount} of ${total} setup steps — want help finishing?`;
  }
  return <Bubble name={name} line={line} />;
};

export const DanaGreeting: React.FC<{
  page: string;
  role: 'admin' | 'manager' | 'employee';
  name?: string;
}> = ({ page, role, name }) =>
  role === 'admin' ? <AdminGreeting page={page} name={name} /> : <BasicGreeting page={page} name={name} />;

export default DanaGreeting;
